import { sentryCaptureException, SENTRY_LEVEL } from 'sentry';
import * as clientErrTypes from 'config/clientErrorTypes';
import { IS_INIT_API_TIMEOUT_MONITOR_ENABLED } from 'config/debug';
import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import { nowMs, getClockSource } from 'client/utils/initApiTimeout/clock';
import {
    getPagePresenceSnapshot,
    hasLeftPage,
    markRequestStart
} from 'client/utils/initApiTimeout/pagePresence';
import { classifyTimeoutCause } from 'client/utils/initApiTimeout/classifyTimeoutCause';

/**
 * Init API 逾時監控。
 *
 * 目的只有一個：讓 Sentry 捕捉「使用者**仍停留在頁面上**，但 API 呼叫後 N 秒仍未回應」的
 * 可能原因（網路異常、後端異常、卡在 access token…）。使用者在監控窗口內離開過頁面
 * （切分頁 / 切 App / 關閉 / bfcache）就完全不記錄——那類逾時是 timer throttling 造成的假警報，
 * 對診斷沒有價值。「離開頁面」的定義集中在 client/utils/initApiTimeout/pagePresence.js。
 *
 * 純遙測：不中斷、不改變 API 的執行結果。
 */

// 計時來源獨立成檔供 pagePresence 共用；這裡 re-export 讓既有 import 路徑不變
export { nowMs, getClockSource };

/**
 * 每次 page load 最多回報幾筆逾時。
 * init 階段是一連串序列 API，後端一慢就會連環噴（一個使用者一次載入報 5~10 筆），
 * 但第 2 筆之後的資訊價值極低——它們只是同一次壅塞的回音。取前幾筆即可還原現場。
 * 被「離開頁面」抑制的逾時不計入預算。
 */
const MAX_TIMEOUT_REPORTS_PER_PAGE_LOAD = 3;

/**
 * 逾時後「最終結局」事件的取樣率。
 * 這是唯一能回答「15 秒之後到底是 20 秒回來還是根本沒回來」的資料。
 *
 * 目前設為 1（全數回報）：結局事件只在「該次逾時確實有送出」時才產生，量已經被
 * MAX_TIMEOUT_REPORTS_PER_PAGE_LOAD 鎖在每次 page load 最多 3 筆，再取樣等於扔掉最想要的資料。
 * 若日後事件量成為問題，調小這個值即可（extraInfo.sampleRate 會一起帶出去，方便回推母體）。
 */
const OUTCOME_REPORT_SAMPLE_RATE = 1;

// 本次 page load 已回報的逾時筆數（module scope，跨 monitor instance 共用）
let timeoutReportCount = 0;

/**
 * 重置本次 page load 的回報預算。**僅供測試使用**，正式流程靠 page load 自然重置。
 */
export function resetTimeoutReportBudget() {
    timeoutReportCount = 0;
}

/**
 * 建立 API 監控函數
 *
 * 總開關關閉時（IS_INIT_API_TIMEOUT_MONITOR_ENABLED === false）回傳一個純轉發函數：
 * 不計時、不掛 page presence listener、不送任何 Sentry 事件。開關放在這裡而非各呼叫端，
 * 是為了讓 src/index.js 的 boot 監控與 useInitPromo 的 init 監控共用同一個判斷。
 *
 * @param {number} timeoutThreshold - 超時閾值（毫秒），預設 15000ms (15秒)
 * @returns {Function} 監控函數
 */
export function createApiMonitor(timeoutThreshold = 15000) {
    if (!IS_INIT_API_TIMEOUT_MONITOR_ENABLED) {
        return function passThroughFetches(originalFetches, path, optionsGetter, config) {
            return originalFetches(path, optionsGetter, config);
        };
    }

    /**
     * 監控 API 調用
     * @param {Function} originalFetches - 原始的 fetches 函數
     * @param {string} path - API 路徑
     * @param {*} optionsGetter - options getter
     * @param {*} config - fetches config
     * @returns {Promise} API 調用的 Promise
     */
    return function monitorFetches(originalFetches, path, optionsGetter, config) {
        const startTimeMs = nowMs();
        const clockSource = getClockSource();
        const requestId = generateRequestId();
        // 在 API 發出時就擷取 URL；timeout callback 觸發時若 SPA 已換頁，URL 才能仍對應到「發出 API 的那個頁面」
        const startedUrl =
            typeof window !== 'undefined' && window.location ? window.location.href : '';
        let timeoutId = null;
        let isRecorded = false; // 逾時已觸發，避免重複記錄
        let isReported = false; // 逾時確實有送出 Sentry 事件（未被離開頁面抑制、未被回報預算擋掉）
        let suspectedCause = null; // 逾時當下結論出的可能原因；供結局事件對照

        // 「離開頁面」判定的起點。頁面在監控窗口內被切到背景 / 卸載時，
        // timer 會被 throttle 甚至凍結，逾時不代表 API 慢，一律不記錄。
        const presenceToken = markRequestStart();

        // 提取請求參數
        const requestParams = extractRequestParams(path);

        // 供 timeout callback 使用（callback 內無法 await）
        let requestBody = null;
        let requestMethod = 'GET';

        // fetch 階段時間（由 originalFetches 透過 config.onRequestSent / onHeaders / onBodyDone 回填）。
        // 這是逾時當下唯一拿得到的階段資訊：Resource Timing entry 要等 body 讀完才會進 timeline，
        // in-flight 時查不到；跨來源沒有 Timing-Allow-Origin 時即使查到也只剩 start / end。
        const fetchTiming = {
            requestSentAtMs: null, // fetch() 真正被呼叫的時間（此前都在解 optionsGetter / access token）
            headersReceivedAtMs: null, // fetch() Promise resolve 時間（≈ TTFB 結束）
            bodyDoneAtMs: null // response body 解析完成時間
        };
        const recordRequestSent = () => {
            if (fetchTiming.requestSentAtMs == null) {
                fetchTiming.requestSentAtMs = nowMs();
            }
        };
        // response headers 抵達時擷取的中繼資訊（httpStatus / 後端 trace id / 回應大小）。
        // 逾時事件只能證明「15 秒時還沒回來」，唯有這些欄位能把事件接到後端 log 上，
        // 也才分得出「resolved 但其實是 502」與「resolved 且真的成功」。
        let responseMeta = null;
        const recordHeadersReceived = response => {
            if (fetchTiming.headersReceivedAtMs == null) {
                fetchTiming.headersReceivedAtMs = nowMs();
            }
            if (responseMeta == null) {
                responseMeta = extractResponseMeta(response);
            }
        };
        const recordBodyDone = () => {
            if (fetchTiming.bodyDoneAtMs == null) {
                fetchTiming.bodyDoneAtMs = nowMs();
            }
        };

        // 將 optionsGetter 包成 memoized 版本：originalFetches 與 body 擷取共用同一個 Promise
        // 避免具副作用的 getter（如 tokenManager.getAccessToken）被觸發兩次
        let memoizedOptionsGetter = optionsGetter;
        if (typeof optionsGetter === 'function') {
            let optionsPromise = null;
            memoizedOptionsGetter = () => {
                if (!optionsPromise) {
                    optionsPromise = Promise.resolve()
                        .then(() => optionsGetter())
                        .then(options => {
                            requestMethod = options?.method || 'GET';
                            if (options?.body) {
                                if (typeof options.body === 'string') {
                                    try {
                                        requestBody = JSON.parse(options.body);
                                    } catch {
                                        requestBody = options.body;
                                    }
                                } else {
                                    requestBody = options.body;
                                }
                            }
                            return options;
                        });
                }
                return optionsPromise;
            };
            // 主動 prime 一次，啟動 body 擷取的 promise chain
            // 即使 originalFetches 因故未消費 getter，timeout callback 仍能取得 body
            // 重複呼叫共用同一個 promise，不會增加副作用次數
            memoizedOptionsGetter().catch(() => {});
        }

        // 建立 timeout 監控
        // 重要：timeout 只記錄錯誤，不會中斷 API 調用
        // 即使超時了，API 還是會繼續執行並返回結果
        timeoutId = setTimeout(() => {
            if (isRecorded) return; // 避免重複記錄

            const elapsedTime = nowMs() - startTimeMs;
            if (elapsedTime < timeoutThreshold) return; // 僅在確實超過閾值才觸發，避免提前觸發

            isRecorded = true;

            // 離開頁面就不記錄：不送事件、不消耗回報預算（這不是「同一次壅塞的回音」，
            // 而是根本不該算進來的假逾時，不該排擠真正的網路 / 後端異常）
            if (hasLeftPage(presenceToken)) return;

            // 回報預算：超過上限就只標記逾時、不再送 Sentry（同一次壅塞的回音沒有診斷價值）
            timeoutReportCount += 1;
            if (timeoutReportCount > MAX_TIMEOUT_REPORTS_PER_PAGE_LOAD) return;
            isReported = true;

            const context = getEnvironmentContext({ startedUrl });

            // 在超時時取得 body
            let finalRequestBody = requestBody;
            let finalMethod = requestMethod;

            // 如果 optionsGetter 不是函數，直接同步取得 body
            if (typeof optionsGetter !== 'function' && optionsGetter && optionsGetter.body) {
                try {
                    if (typeof optionsGetter.body === 'string') {
                        try {
                            finalRequestBody = JSON.parse(optionsGetter.body);
                        } catch {
                            finalRequestBody = optionsGetter.body;
                        }
                    } else {
                        finalRequestBody = optionsGetter.body;
                    }
                    finalMethod = optionsGetter.method || finalMethod;
                } catch (error) {
                    // 忽略錯誤，使用預先取得的資訊（如果有的話）
                }
            }

            const fetchInstrumented = buildFetchInstrumentedTiming(fetchTiming, startTimeMs);
            const networkInfo = getNetworkInfo();
            const finalResponseMeta = responseMeta ?? {
                available: false,
                reason: 'headers-not-received'
            };
            // 把原料結論成一個「可能原因」。它同時進 error name（issue 標題第一行）、
            // fingerprint（不同原因分成不同 issue）與 tag（可統計分布）
            suspectedCause = classifyTimeoutCause({ fetchInstrumented, networkInfo });

            // 建立 Sentry 錯誤物件
            // message 用正規化後的 path（UUID / 流水號換成 placeholder），避免每個活動各自
            // 撐出一個 issue；原始 path 完整保留在 extraInfo.apiPath
            const normalizedPath = normalizeApiPathForFingerprint(path);
            const timeoutError = new Error(
                `[Init API Timeout] ${normalizedPath} exceeded ${timeoutThreshold}ms`
            );
            // Sentry issue 標題：第一行 name 回答「為什麼」，第二行 message 回答「哪支 API」
            timeoutError.name = `InitApiTimeout-${suspectedCause}`;
            timeoutError.isActivelyCatchErr = true;

            timeoutError.extraInfo = {
                errType: clientErrTypes.INIT_API_TIMEOUT,
                apiTimeout: true,
                requestId,
                apiPath: path,
                method: finalMethod || 'GET',
                requestParams,
                requestBody: maskSensitiveBody(finalRequestBody),
                thresholdMs: timeoutThreshold,
                triggeredAt: new Date().toISOString(),
                clockSource: clockSource,
                elapsedAtCallbackMs: Math.round(elapsedTime),
                callbackDelayMs: Math.round(elapsedTime - timeoutThreshold),
                // 本次 page load 的第幾筆逾時；> MAX_TIMEOUT_REPORTS_PER_PAGE_LOAD 的不會送出
                reportSequence: timeoutReportCount,
                reportBudget: MAX_TIMEOUT_REPORTS_PER_PAGE_LOAD,
                // 逾時當下的可能原因（見 classifyTimeoutCause）
                suspectedCause,
                environment: context,
                // available=false 代表環境沒有 document，「離開頁面」判定未生效
                pagePresence: getPagePresenceSnapshot(),
                networkInfo,
                // 唯一的階段來源：從 fetch Promise 切點量測，不受跨來源 TAO 限制
                fetchInstrumented,
                // headers 若已抵達（inflightStage 為 downloading / completed-callback-late），
                // 這裡就有 status 與後端 trace id 可以往後端查
                responseMeta: finalResponseMeta
            };

            // 記錄到 Sentry（使用 try-catch 確保不會中斷流程）
            try {
                const errorObj = generateApiErrObj(timeoutError, clientErrTypes.INIT_API_TIMEOUT);
                sentryCaptureException(errorObj, {
                    // 逾時不代表失敗（請求並未被中止，多數最終仍會回來），降為 warning
                    // 以免與真正的故障混在同一層級
                    level: SENTRY_LEVEL.WARNING,
                    // 同一支 API 的不同原因各自成一個 issue，可分別設 alert、分別追蹤趨勢
                    fingerprint: ['init-api-timeout', suspectedCause, normalizedPath],
                    tags: {
                        ...buildCommonTags({
                            normalizedPath,
                            suspectedCause,
                            networkInfo,
                            responseMeta: finalResponseMeta
                        }),
                        // 逾時當下卡在哪一階段（suspected_cause 的原料之一）
                        inflight_stage: fetchInstrumented?.inflightStage
                    }
                });
            } catch (sentryError) {
                // 即使 Sentry 失敗也不影響原有流程
                console.error('Sentry reporting failed:', sentryError);
            }
        }, timeoutThreshold);

        /**
         * 逾時事件只能證明「15 秒時還沒回來」，永遠問不出真實耗時，也不知道最後成功與否。
         * 這裡在請求真正 settle 時補一筆結局事件（取樣），用 requestId 與逾時事件對得起來。
         * 只在「該次逾時確實有送出」且「逾時後到 settle 之間沒有離開頁面」時才回報：
         * 前者是為了兩邊對得上、不白白放大事件量；後者是因為背景 throttle 會污染 resolvedAfterMs。
         * @param {'resolved'|'rejected'} outcome
         * @param {Error} [error]
         */
        function reportOutcome(outcome, error) {
            if (!isReported) return;
            if (hasLeftPage(presenceToken)) return;
            // 弱掃誤判：這裡的 Math.random() 只用來對「結局事件」做取樣（控制送往 Sentry 的事件量），
            // 非 token、密碼或加密種子，不需要 CSPRNG。
            // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
            if (Math.random() >= OUTCOME_REPORT_SAMPLE_RATE) return;

            try {
                const resolvedAfterMs = Math.round(nowMs() - startTimeMs);
                const normalizedPath = normalizeApiPathForFingerprint(path);
                const networkInfo = getNetworkInfo();
                const finalResponseMeta = responseMeta ?? {
                    available: false,
                    reason: 'headers-not-received'
                };
                const outcomeError = new Error(
                    `[Init API Timeout Outcome] ${normalizedPath} ${outcome} after timeout`
                );
                outcomeError.name = 'InitApiTimeoutOutcome';
                outcomeError.isActivelyCatchErr = true;
                outcomeError.extraInfo = {
                    errType: clientErrTypes.INIT_API_TIMEOUT_OUTCOME,
                    requestId, // 對應同一筆 Init-Api-Timeout 事件
                    apiPath: path,
                    outcome,
                    // 逾時當下結論出的原因，方便與逾時事件對照
                    suspectedCause,
                    thresholdMs: timeoutThreshold,
                    resolvedAfterMs,
                    overThresholdMs: Math.max(0, resolvedAfterMs - timeoutThreshold),
                    sampleRate: OUTCOME_REPORT_SAMPLE_RATE,
                    fetchInstrumented: buildFetchInstrumentedTiming(fetchTiming, startTimeMs),
                    // outcome=resolved 不等於成功：httpStatus 才分得出 200 與 502
                    responseMeta: finalResponseMeta,
                    pagePresence: getPagePresenceSnapshot(),
                    networkInfo,
                    ...(error ? { errorName: error.name, errorMessage: error.message } : {})
                };

                sentryCaptureException(
                    generateApiErrObj(outcomeError, clientErrTypes.INIT_API_TIMEOUT_OUTCOME),
                    {
                        // 純遙測，不是故障
                        level: SENTRY_LEVEL.INFO,
                        fingerprint: ['init-api-timeout-outcome', normalizedPath, outcome],
                        tags: {
                            ...buildCommonTags({
                                normalizedPath,
                                suspectedCause,
                                networkInfo,
                                responseMeta: finalResponseMeta
                            }),
                            outcome,
                            // 這個 tag 就是 outcome 事件存在的理由：
                            // 直接在 Sentry 上看出「逾時之後究竟是 20 秒回來還是 60 秒以上」
                            resolved_after_bucket: bucketResolvedAfterMs(resolvedAfterMs)
                        }
                    }
                );
            } catch (sentryError) {
                console.error('Sentry outcome reporting failed:', sentryError);
            }
        }

        // 執行原始 fetches
        // API 調用與 timeout 監控是獨立的，即使 timeout 觸發了，API 還是會繼續執行
        // 注入 onRequestSent / onHeaders / onBodyDone hooks 讓 originalFetches 在三個關鍵時間點通知我們
        const configWithHooks = {
            ...(config || {}),
            onRequestSent: recordRequestSent,
            onHeaders: recordHeadersReceived,
            onBodyDone: recordBodyDone
        };
        return originalFetches(path, memoizedOptionsGetter, configWithHooks)
            .then(response => {
                // API 成功完成，清除 timeout（如果還沒觸發）
                clearTimeout(timeoutId);
                reportOutcome('resolved');
                return response;
            })
            .catch(error => {
                // API 錯誤，清除 timeout（如果還沒觸發）
                clearTimeout(timeoutId);
                const elapsedTime = nowMs() - startTimeMs;
                reportOutcome('rejected', error);

                // 在錯誤資訊中加入執行時間（如果還沒記錄過）
                if (!isRecorded && error.extraInfo) {
                    error.extraInfo.apiElapsedTime = Math.round(elapsedTime);
                    error.extraInfo.apiPath = path;
                }
                // 將原始錯誤繼續往上拋，保持原有的錯誤處理邏輯
                throw error;
            });
    };
}

/**
 * 產生唯一 requestId，優先使用 crypto.randomUUID，不可用時 fallback
 * @returns {string}
 */
function generateRequestId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // 弱掃誤判：這是 crypto.randomUUID 不可用時的 fallback，requestId 僅用於把前端逾時事件
    // 與 Sentry 上的同一次請求對起來（前端自用的追蹤碼），非 token、密碼或加密種子，不需要 CSPRNG。
    // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * 組出逾時 / 結局兩種事件共用的 Sentry tags。
 *
 * 為什麼要有 tags：這些值本來就都在 `extraInfo` 裡，但 `extra` 不會被 Sentry 建索引——
 * 只有點進單一事件才看得到，無法搜尋也無法 group by。同樣的欄位放進 tags 之後，
 * 才能在 issue 頁面直接看到分布（例：有幾成逾時是卡在 access token 而不是這支 API）。
 *
 * ⚠️ 只放低基數欄位。requestId / promoUUID 這類每筆或每活動都不同的值會撐爆 tag 索引，
 * 留在 `extra` 就好。
 *
 * @returns {Object<string, *>} 值可能是 undefined，由 sentryCaptureException 統一濾除
 */
function buildCommonTags({ normalizedPath, suspectedCause, networkInfo, responseMeta }) {
    return {
        // 已正規化（UUID / 流水號換成 placeholder），基數只有十幾種
        api_path: normalizedPath,
        // 逾時當下結論出的可能原因（6 種），是排查時最先看的切片
        suspected_cause: suspectedCause,
        effective_type: networkInfo?.effectiveType,
        on_line: networkInfo?.navigatorAvailable ? networkInfo.onLine : undefined,
        // headers 尚未抵達時為 undefined；有值就分得出 200 與 5xx
        http_status: responseMeta?.httpStatus
    };
}

/**
 * 把「從發出到真正 settle 共花了多久」分桶成低基數的 tag 值。
 *
 * 數字放在 `extra` 裡 Sentry 無法聚合，分桶後才看得到分布——而這正是結局事件存在的目的：
 * 回答「逾時之後究竟是 20 秒回來，還是根本要等到一分鐘以上」。
 *
 * 分界採固定秒數（非相對於 timeoutThreshold），這樣即使日後調整閾值，歷史資料仍可比較。
 *
 * @param {number} resolvedAfterMs
 * @returns {string}
 */
export function bucketResolvedAfterMs(resolvedAfterMs) {
    if (!Number.isFinite(resolvedAfterMs)) return 'unknown';

    const seconds = resolvedAfterMs / 1000;
    if (seconds < 20) return '<20s';
    if (seconds < 30) return '20-30s';
    if (seconds < 60) return '30-60s';
    if (seconds < 120) return '60-120s';
    return '>=120s';
}

/**
 * 可能帶有後端關聯 ID 的 response header（依常見程度排序）。
 * requestId 是前端自己生的，只能把逾時事件與 outcome 事件串起來，後端查不到；
 * 這些 header 才是唯一能跨到後端 log / APM 的鍵。
 */
const CORRELATION_HEADER_KEYS = [
    'x-request-id',
    'request-id',
    'x-correlation-id',
    'x-amzn-trace-id',
    'x-amz-request-id',
    'cf-ray',
    'traceparent',
    'x-trace-id'
];

/**
 * 從原始 Response 擷取可診斷的中繼資訊。
 *
 * ⚠️ 跨來源時 `headers.get()` 只讀得到後端以 `Access-Control-Expose-Headers` 開放的 header，
 * 其餘一律回 `null`（不會 throw）。因此「一個 trace id 都沒拿到」通常代表 header 沒被開放，
 * 而不是後端沒送——用 correlationAvailable 把這個區別留在事件裡，才不會誤判成後端沒有 trace。
 *
 * @param {Response} [response] - fetch() resolve 的原始 Response
 * @returns {object} available=false 時帶 reason
 */
export function extractResponseMeta(response) {
    if (!response) return { available: false, reason: 'no-response' };

    const readHeader = name => {
        try {
            if (!response.headers || typeof response.headers.get !== 'function') return null;
            return response.headers.get(name);
        } catch {
            return null;
        }
    };

    const meta = { available: true };

    if (typeof response.status === 'number') meta.httpStatus = response.status;
    if (typeof response.ok === 'boolean') meta.ok = response.ok;
    // 'opaque' 代表這是 no-cors 回應，任何 header 與 status 都讀不到
    if (typeof response.type === 'string') meta.responseType = response.type;
    if (typeof response.redirected === 'boolean') meta.redirected = response.redirected;

    const correlationIds = {};
    for (const key of CORRELATION_HEADER_KEYS) {
        const value = readHeader(key);
        if (value) correlationIds[key] = value;
    }
    meta.correlationAvailable = Object.keys(correlationIds).length > 0;
    if (meta.correlationAvailable) {
        meta.correlationIds = correlationIds;
    } else {
        meta.correlationNote =
            'no trace header readable; backend may need Access-Control-Expose-Headers';
    }

    // 後端 Server-Timing：有的話可直接得到 server 端耗時，免去前後端對時的問題
    const serverTiming = readHeader('server-timing');
    if (serverTiming) meta.serverTiming = serverTiming;

    // 分辨「後端查詢慢」與「回應太大下載慢」
    const contentLength = readHeader('content-length');
    if (contentLength != null) {
        const parsed = Number(contentLength);
        meta.contentLength = Number.isFinite(parsed) ? parsed : contentLength;
    }
    const contentEncoding = readHeader('content-encoding');
    if (contentEncoding) meta.contentEncoding = contentEncoding;

    return meta;
}

// 視為敏感資料的欄位名稱 pattern（不分大小寫）
const SENSITIVE_KEY_PATTERN = /^(password|passwd|pwd|secret|token|.+_token|auth(?:orization)?|api[_-]?key|phone|mobile|tel|email|mail|ssn|id[_-]?(card|number)|credit[_-]?card|card[_-]?number|cvv|cvc|coordinates|lat(itude)?|lng|lon(gitude)?|address)$/i;

/**
 * 對單一敏感值做部分遮罩：字串保留前 4 字元 + '****'；其他型別（數字 / 陣列 / 物件）一律 '[MASKED]'
 * 之所以不對數字 / GPS 陣列做 partial，是因為前綴本身就足以洩露位置或識別資訊
 * @param {*} value
 * @returns {*}
 */
function partialMask(value) {
    if (value == null) return value;
    if (typeof value === 'string') {
        if (value.length <= 4) return '****';
        return `${value.slice(0, 4)}****`;
    }
    return '[MASKED]';
}

/**
 * 對 request body 進行 PII 遮罩：依欄位名將敏感欄位的值部分遮罩，其餘原樣保留
 * @param {*} value - 任意 body 值
 * @returns {*} 已遮罩的副本（不變更原物件）
 */
export function maskSensitiveBody(value) {
    if (Array.isArray(value)) return value.map(maskSensitiveBody);
    if (value && typeof value === 'object') {
        const out = {};
        for (const key of Object.keys(value)) {
            out[key] = SENSITIVE_KEY_PATTERN.test(key)
                ? partialMask(value[key])
                : maskSensitiveBody(value[key]);
        }
        return out;
    }
    return value;
}

/**
 * 從 fetch Promise 的切點量測階段時間，做為逾時當下唯一的階段來源。
 * 不依賴 Resource Timing API：in-flight 的請求根本還沒有 entry，跨來源沒有 Timing-Allow-Origin 時
 * 即使有 entry 也只剩 start / end。
 *
 * inflightStage 含意：
 *  - pre-request-dispatch: fetch() 都還沒被呼叫，仍卡在 optionsGetter
 *    （多半是 tokenManager.getAccessToken() 去打 /auth/client/token）。
 *    這段時間過去會被誤記成本 API 在等 server，是最主要的歸因錯誤來源。
 *  - waiting-server: 請求已送出但 response headers 還沒抵達（DNS/connect/TTFB 全段）
 *  - downloading: headers 已抵達，body 還在下載
 *  - completed-callback-late: API 已完成但 timeout callback 仍觸發（罕見，代表 setTimeout 嚴重延遲）
 *
 * preFetchMs 含意：從監控開始計時到 fetch() 真正被呼叫之間的耗時。
 * 它必須從總耗時裡扣掉，才是這支 API 真正的網路時間。
 *
 * @param {{ requestSentAtMs: number|null, headersReceivedAtMs: number|null, bodyDoneAtMs: number|null }} timing
 * @param {number} startTimeMs - 開始監控的時間（performance.now 座標系）
 * @returns {object}
 */
function buildFetchInstrumentedTiming(timing, startTimeMs) {
    const { requestSentAtMs, headersReceivedAtMs, bodyDoneAtMs } = timing;

    // fetch() 尚未被呼叫：整段時間都花在解 optionsGetter / 取 access token，
    // 與這支 API 的後端延遲無關
    if (requestSentAtMs == null) {
        return {
            available: true,
            inflightStage: 'pre-request-dispatch',
            note: 'fetch() not called yet; still resolving request options / access token'
        };
    }

    const preFetchMs = Math.max(0, Math.round(requestSentAtMs - startTimeMs));

    if (headersReceivedAtMs == null) {
        return {
            available: true,
            inflightStage: 'waiting-server',
            preFetchMs,
            // 真正花在等 server 的時間（已扣除 preFetchMs）
            waitingElapsedMs: Math.max(0, Math.round(nowMs() - requestSentAtMs)),
            note: 'response headers not received at timeout'
        };
    }

    const requestPhaseMs = Math.max(0, Math.round(headersReceivedAtMs - startTimeMs));

    if (bodyDoneAtMs == null) {
        return {
            available: true,
            inflightStage: 'downloading',
            preFetchMs,
            requestPhaseMs,
            // 已扣除 preFetchMs 的 TTFB
            waitingPhaseMs: Math.max(0, Math.round(headersReceivedAtMs - requestSentAtMs)),
            downloadElapsedMs: Math.max(0, Math.round(nowMs() - headersReceivedAtMs))
        };
    }

    return {
        available: true,
        inflightStage: 'completed-callback-late',
        preFetchMs,
        requestPhaseMs,
        waitingPhaseMs: Math.max(0, Math.round(headersReceivedAtMs - requestSentAtMs)),
        downloadPhaseMs: Math.max(0, Math.round(bodyDoneAtMs - headersReceivedAtMs)),
        totalMs: Math.max(0, Math.round(bodyDoneAtMs - startTimeMs))
    };
}

/**
 * 從 URL path 抽出常見的識別參數（method 不在 path 中，不在此處取得）
 * @param {string} path
 * @returns {Record<string, string>}
 */
function extractRequestParams(path) {
    const params = {};

    const promoMatch = path.match(/\/promotions\/([^/]+)/);
    if (promoMatch) params.promoUUID = promoMatch[1];

    const entryMatch = path.match(/\/entries\/([^/]+)/);
    if (entryMatch) params.entryUUID = entryMatch[1];

    const formMatch = path.match(/\/forms\/([^/]+)/);
    if (formMatch) params.formId = formMatch[1];

    const lotteryMatch = path.match(/\/lotteries\/([^/]+)/);
    if (lotteryMatch) params.lotteryUUID = lotteryMatch[1];

    return params;
}

/**
 * 取得環境資訊
 * @param {{ startedUrl?: string }} [opts] - startedUrl: API 發出時的 URL；
 *   未提供則 fallback 為當下 URL（保留向後相容）
 * @returns {{ url: string, currentUrl: string, userAgent: string, visibilityState: string, timestamp: string }}
 *   - url: API 發出時的頁面 URL（用於排查「來源頁面」）
 *   - currentUrl: 事件觸發當下的 URL（與 url 不同代表 SPA 在 timeout 期間換頁了；
 *     SPA 換頁不算離開頁面，請求仍在真實等待）
 *   - visibilityState: 事件觸發當下的頁面可見狀態（能送出的事件必為 visible）
 */
function getEnvironmentContext({ startedUrl } = {}) {
    const currentUrl = typeof window !== 'undefined' && window.location ? window.location.href : '';
    return {
        url: startedUrl || currentUrl,
        currentUrl,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        timestamp: new Date().toISOString()
    };
}

/**
 * 把 API path 正規化成穩定的分組指紋：移除 query string，並把 UUID / 純數字 ID 換成 placeholder。
 *
 * 為什麼需要：Sentry 預設用 message 分組，而 message 內含 promo UUID 時，同一種問題會被拆成
 * 數百個 issue（實測 7 天內 40 個 error.value 全是同一件事），完全無法聚合與排序。
 *
 * @param {string} path - 原始 API path，可含 query string
 * @returns {string} 例：/promotions/{uuid}/entries、/forms/{id}
 */
export function normalizeApiPathForFingerprint(path) {
    return String(path ?? '')
        .split('?')[0]
        .replace(
            /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi,
            '/{uuid}'
        )
        .replace(/\/\d+(?=\/|$)/g, '/{id}');
}

/**
 * 取得網路資訊
 */
export function getNetworkInfo() {
    // 1. 先檢查 navigator 是否存在
    if (typeof navigator === 'undefined') {
        return { navigatorAvailable: false };
    }

    const baseInfo = {
        navigatorAvailable: true,
        onLine: navigator.onLine
    };

    // 2. 檢查 navigator.connection（部分瀏覽器不支援）
    const connection =
        navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (connection) {
        return {
            ...baseInfo,
            effectiveType: connection.effectiveType,
            downlink: connection.downlink,
            rtt: connection.rtt,
            saveData: connection.saveData
        };
    }

    return baseInfo;
}
